import { ITableRow, ITableViewColumn, ICellValue, IUpdateTableViewCell, ITableViewCell } from 'types'
import { updateRowPosition } from 'components/spreadsheet/contexts/data/actions'
import {
  ISpreadsheetData,
  IViewCoordinate,
  IViewCoordinateMap,
  ITransposedResult,
  IViewPortColumn,
  SpreadsheetReducerActions
} from 'components/spreadsheet/types'
import { CompressedColumnStore } from 'components/spreadsheet/helpers/compressed-column-store'
import { rowDataArrayToObject, getOriginalData, IOriginalData } from 'components/spreadsheet/helpers/functions'

export const FIELD_COLUMN_ID = '__transpose_field__'
export const DEFAULT_TRANSPOSE_COLUMN_WIDTH = 150
/**
 * How many of the view's rows the transposed grid shows as columns. Every row becomes a column
 * object and a cell in each transposed row, so a million-row table cannot be transposed whole:
 * it ran out of memory while the pivot column was being chosen. Past this the grid shows the
 * first rows and says so (ITransposedResult.truncations).
 */
export const MAX_TRANSPOSE_COLUMNS = 5000

const PIVOT_COLUMN_PREFIX = '__pivot_'
const TRANSPOSED_ROW_PREFIX = '__tr_'
const SYNTHETIC_ID_SUFFIX = '__'

const makePivotColumnId = (originalRowId: string): string =>
  `${PIVOT_COLUMN_PREFIX}${originalRowId}${SYNTHETIC_ID_SUFFIX}`
const makeTransposedRowId = (originalColumnId: string): string =>
  `${TRANSPOSED_ROW_PREFIX}${originalColumnId}${SYNTHETIC_ID_SUFFIX}`

const stripSyntheticId = (id: string, prefix: string): string | null =>
  id.startsWith(prefix) && id.endsWith(SYNTHETIC_ID_SUFFIX)
    ? id.slice(prefix.length, id.length - SYNTHETIC_ID_SUFFIX.length)
    : null

/**
 * The transposed grid's cell → source cell lookup, worked out from the ids. A transposed row id
 * carries its source column id and a transposed column id its source row id, so there is nothing
 * to store: the Map this replaces held one entry per cell, rows × columns of them.
 */
export class TransposeCoordinateMap implements IViewCoordinateMap {
  constructor(
    private readonly originalColumnIds: Set<string>,
    private readonly originalRowIds: Set<string>,
    // Source columns the view has no index for: their cells exist but map to no source cell.
    private readonly unmappedColumnIds: Set<string>
  ) {}

  get size(): number {
    return this.originalColumnIds.size * (this.originalRowIds.size + 1)
  }

  get(key: string): IViewCoordinate | undefined {
    const split = key.indexOf(':')
    if (split === -1) return undefined
    const originalColumnId = stripSyntheticId(key.slice(0, split), TRANSPOSED_ROW_PREFIX)
    if (originalColumnId === null || !this.originalColumnIds.has(originalColumnId)) return undefined
    const columnId = key.slice(split + 1)
    if (columnId === FIELD_COLUMN_ID) return { originalRowId: null, originalColumnId }
    const originalRowId = stripSyntheticId(columnId, PIVOT_COLUMN_PREFIX)
    if (originalRowId === null || !this.originalRowIds.has(originalRowId)) return undefined
    if (this.unmappedColumnIds.has(originalColumnId)) return { originalRowId: null, originalColumnId: null }
    return { originalRowId, originalColumnId }
  }

  has(key: string): boolean {
    return this.get(key) !== undefined
  }
}

/**
 * Read a cell value from either the rowData array or the CompressedColumnStore.
 */
function getCellValue(row: ITableRow, columnIndex: number, columnStore: CompressedColumnStore | null): ICellValue {
  if (row.rowData) {
    return row.rowData[columnIndex] ?? null
  }
  if (columnStore && row._rowIndex >= 0) {
    return columnStore.get(row._rowIndex, columnIndex)
  }
  return null
}

/**
 * Create a synthetic ITableViewColumn with sensible defaults.
 */
function makeSyntheticColumn(publicId: string, name: string, sortOrder: number, width: number): ITableViewColumn {
  return {
    publicId,
    name,
    isIndexed: false,
    width,
    kind: 'text',
    description: null,
    script: null,
    scriptEnabled: false,
    formula: null,
    formulaEnabled: false,
    aggregate: 0,
    isJoined: false,
    thousandSeparator: false,
    displayLink: false,
    decimalPlaces: 0,
    dateFormat: null,
    defaultValue: null,
    headerBackgroundColor: '',
    headerTextColor: '',
    exportWidth: null,
    isSynced: false,
    sortOrder,
    required: false,
    stringValidation: null,
    validationMessage: null,
    hardValidation: false,
    validationNoBlanks: false,
    validationNoDuplicates: false,
    displayValidationError: false,
    blockInvalidSelectValues: false,
    formWidth: null
  }
}

const EMPTY_RESULT: ITransposedResult = {
  rows: [],
  columns: [],
  columnIdToIndex: new Map(),
  indexToColumnId: [],
  coordinateMap: new Map(),
  truncations: []
}

/**
 * Transform spreadsheet data into a transposed representation based on a pivot column.
 *
 * Given original data with pivot column P and non-pivot columns C1..Cn:
 * - Unique pivot values V1..Vm become the transposed columns (plus a leading "Field" column)
 * - For each repetition group g (0..maxGroupSize-1), one row per non-pivot column is emitted
 * - Each transposed cell maps back to the original (rowId, columnId) via coordinateMap
 */
/** How much of the data to materialise; the grid uses the defaults, an export turns the limit off. */
export interface TransposeLimits {
  maxColumns?: number
}

export function getTransposedData(
  spreadsheetData: ISpreadsheetData,
  pivotColumnId: string,
  limits: TransposeLimits = {}
): ITransposedResult {
  const maxColumns = limits.maxColumns ?? MAX_TRANSPOSE_COLUMNS
  const { columnIdToIndex, columnStore, rows, visibleBitmap, viewDetails } = spreadsheetData

  // 1. Find the pivot column index
  const pivotColIndex = columnIdToIndex.get(pivotColumnId)
  if (pivotColIndex === undefined) return EMPTY_RESULT

  // 2. Get non-pivot columns in their original display order
  const hiddenColumns = spreadsheetData.userConfiguration.hiddenColumns
  const nonPivotColumns = viewDetails.columns.filter(
    (col) => col.publicId !== pivotColumnId && !hiddenColumns.includes(col.publicId)
  )

  // 3. Collect visible rows, respecting visibleBitmap — the first maxColumns of them
  const visibleRows: ITableRow[] = []
  let visibleCount = 0
  for (let i = 0; i < rows.length; i++) {
    if (visibleBitmap && !visibleBitmap[i]) continue
    visibleCount++
    if (visibleRows.length < maxColumns) visibleRows.push(rows[i])
  }
  if (visibleRows.length === 0) return EMPTY_RESULT
  const truncations =
    visibleCount > visibleRows.length
      ? [
          {
            shown: visibleRows.length,
            total: visibleCount,
            message: `Showing the first ${visibleRows.length.toLocaleString()} of ${visibleCount.toLocaleString()} rows as columns. Filter the view to see other rows.`
          }
        ]
      : []

  // 4. Build transposed columns — one per original row (plus leading Field column)
  const transposedColumns: ITableViewColumn[] = []
  const transposedColumnIdToIndex = new Map<string, number>()
  const transposedIndexToColumnId: string[] = []

  const fieldWidth = viewDetails.transposeSettings?.fieldColumnWidth ?? DEFAULT_TRANSPOSE_COLUMN_WIDTH
  const dataColumnWidths = viewDetails.transposeSettings?.dataColumnWidths ?? null

  // First column: uses the pivot column's name as its header
  const pivotColumn = viewDetails.columns.find((col) => col.publicId === pivotColumnId)
  const fieldCol = makeSyntheticColumn(FIELD_COLUMN_ID, pivotColumn?.name || 'Field', 0, fieldWidth)
  transposedColumns.push(fieldCol)
  transposedColumnIdToIndex.set(FIELD_COLUMN_ID, 0)
  transposedIndexToColumnId.push(FIELD_COLUMN_ID)

  // One column per original row — use the row's pivot value as the header.
  // Width is looked up by the original row's publicId so each column persists
  // its own width independently (falling back to the default if unset).
  for (let r = 0; r < visibleRows.length; r++) {
    const originalRow = visibleRows[r]
    const pivotVal = getCellValue(originalRow, pivotColIndex, columnStore)
    const displayName = pivotVal != null ? String(pivotVal) : '(empty)'
    const colId = makePivotColumnId(originalRow.publicId)
    const width = dataColumnWidths?.[originalRow.publicId] ?? DEFAULT_TRANSPOSE_COLUMN_WIDTH
    const col = makeSyntheticColumn(colId, displayName, r + 1, width)
    transposedColumns.push(col)
    transposedColumnIdToIndex.set(colId, r + 1)
    transposedIndexToColumnId.push(colId)
  }

  // 5. Build transposed rows — one per non-pivot column
  const transposedRows: ITableRow[] = []
  const unmappedColumnIds = new Set<string>()

  for (let c = 0; c < nonPivotColumns.length; c++) {
    const nonPivotCol = nonPivotColumns[c]
    const nonPivotColIndex = columnIdToIndex.get(nonPivotCol.publicId)

    // Build rowData array: [fieldName, row0Value, row1Value, ...]
    const rowData: ICellValue[] = new Array(visibleRows.length + 1)
    rowData[0] = nonPivotCol.name

    if (nonPivotColIndex !== undefined) {
      for (let r = 0; r < visibleRows.length; r++) {
        rowData[r + 1] = getCellValue(visibleRows[r], nonPivotColIndex, columnStore)
      }
    } else {
      // The "Field" cell still maps to the column; its data cells map to nothing.
      unmappedColumnIds.add(nonPivotCol.publicId)
      for (let r = 0; r < visibleRows.length; r++) rowData[r + 1] = null
    }

    transposedRows.push({
      publicId: makeTransposedRowId(nonPivotCol.publicId),
      sortOrder: c,
      rowData,
      _rowIndex: -1,
      originalColumnId: nonPivotCol.publicId
    })
  }

  const coordinateMap = new TransposeCoordinateMap(
    new Set(nonPivotColumns.map((col) => col.publicId)),
    new Set(visibleRows.map((row) => row.publicId)),
    unmappedColumnIds
  )

  return {
    rows: transposedRows,
    columns: transposedColumns,
    columnIdToIndex: transposedColumnIdToIndex,
    indexToColumnId: transposedIndexToColumnId,
    coordinateMap,
    truncations
  }
}

/**
 * Map transposed cell coordinates back to their original row/column IDs.
 *
 * Returns a new IUpdateTableViewCell with remapped cells, or null if no
 * cells could be mapped (e.g. all edits were on the read-only Field column).
 */
export function mapTransposedCells(
  cells: IUpdateTableViewCell,
  coordinateMap: IViewCoordinateMap
): IUpdateTableViewCell | null {
  const mappedCells: ITableViewCell[] = []
  for (const cell of cells.cells) {
    if (cell.columnId === FIELD_COLUMN_ID) continue
    const coord = coordinateMap.get(`${cell.rowId}:${cell.columnId}`)
    if (coord && coord.originalRowId && coord.originalColumnId) {
      mappedCells.push({
        rowId: coord.originalRowId,
        columnId: coord.originalColumnId,
        value: cell.value
      })
    }
  }
  if (mappedCells.length === 0) return null
  return { cells: mappedCells, context: cells.context }
}

/**
 * Extract the original row public ID from a synthetic transposed column ID.
 * e.g. `__pivot_row-0__` → `row-0`
 */
export function extractRowIdFromPivotColumnId(syntheticColId: string): string {
  return stripSyntheticId(syntheticColId, PIVOT_COLUMN_PREFIX) ?? syntheticColId
}

/**
 * Reorder an original column via the transpose view, where each row stands for one of the original
 * columns. The row-drag counterpart of reorderTransposedColumn.
 *
 * The splice puts the dragged column exactly where the target was, so the dragged row takes the
 * target row's place in either direction.
 */
export function reorderTransposedRow(
  draggedRowId: string,
  targetRowId: string,
  spreadsheetData: ISpreadsheetData,
  setSpreadsheetData: React.Dispatch<SpreadsheetReducerActions>
) {
  const origData = getOriginalData(spreadsheetData)
  const draggedColId = spreadsheetData.rows.find((r) => r.publicId === draggedRowId)?.originalColumnId
  const targetColId = spreadsheetData.rows.find((r) => r.publicId === targetRowId)?.originalColumnId
  if (!draggedColId || !targetColId || draggedColId === targetColId) return

  const cols = [...origData.columns]
  const fromIdx = cols.findIndex((c) => c.publicId === draggedColId)
  const toIdx = cols.findIndex((c) => c.publicId === targetColId)
  if (fromIdx === -1 || toIdx === -1) return

  const [moved] = cols.splice(fromIdx, 1)
  cols.splice(toIdx, 0, moved)
  setSpreadsheetData({ type: 'CHANGE_ALL_COLUMNS_ORDER', columns: cols.map((c, i) => ({ ...c, sortOrder: i })) })
}

/**
 * Reorder an original row via the transpose view.
 *
 * Both the Header drag-and-drop and the Column Order menu call this
 * to translate a transposed column reorder into a row reorder.
 */
export function reorderTransposedColumn(
  movedColumnId: string,
  targetColumnId: string | null,
  spreadsheetData: ISpreadsheetData,
  setSpreadsheetData: React.Dispatch<SpreadsheetReducerActions>,
  projectId: string
) {
  const origData = getOriginalData(spreadsheetData)
  if (!origData.coordinateMap) return

  // Only allow reorder between synthetic pivot columns — ignore the Field column
  if (movedColumnId === FIELD_COLUMN_ID || targetColumnId === FIELD_COLUMN_ID) return
  if (
    !movedColumnId.startsWith(PIVOT_COLUMN_PREFIX) ||
    (targetColumnId && !targetColumnId.startsWith(PIVOT_COLUMN_PREFIX))
  )
    return

  const movedRowId = extractRowIdFromPivotColumnId(movedColumnId)
  const targetRowId = targetColumnId ? extractRowIdFromPivotColumnId(targetColumnId) : null
  if (!movedRowId || movedRowId === targetRowId) return

  const originalData: ISpreadsheetData = {
    ...spreadsheetData,
    rows: origData.rows,
    viewDetails: { ...spreadsheetData.viewDetails, columns: origData.columns },
    indexToColumnId: Array.from(origData.columnIdToIndex.entries())
      .sort((a, b) => a[1] - b[1])
      .map(([colId]) => colId),
    columnIdToIndex: origData.columnIdToIndex,
    columnStore: origData.columnStore
  }

  updateRowPosition(
    projectId,
    movedRowId,
    targetRowId,
    originalData,
    setSpreadsheetData,
    () => {},
    () => {}
  )
}

/**
 * Build per-column original rowData for select dependency resolution in transposed view.
 *
 * In transposed view each column represents an original row, so the dependency lookup
 * needs rowData keyed by *original* column IDs with values from the same original row.
 * Returns a map of transposedColumnId → { originalColumnId: cellValue }.
 */
export function getTransposedDependencyRowData(
  transposedRow: ITableRow,
  viewportColumns: IViewPortColumn[],
  originalData: IOriginalData
): Map<string, Record<string, ICellValue>> | null {
  const {
    rows: originalRows,
    columnIdToIndex: originalColumnIdToIndex,
    columnStore: originalColumnStore,
    coordinateMap
  } = originalData
  const origRowMap = new Map(originalRows.map((r) => [r.publicId, r]))
  const origIndexToColumnId = Array.from(originalColumnIdToIndex.entries())
    .sort((a, b) => a[1] - b[1])
    .map(([colId]) => colId)
  const result = new Map<string, Record<string, ICellValue>>()

  for (const viewportCol of viewportColumns) {
    const key = `${transposedRow.publicId}:${viewportCol.column.publicId}`
    const coord = coordinateMap?.get(key)
    if (!coord?.originalRowId) continue

    const origRow = origRowMap.get(coord.originalRowId)
    if (!origRow) continue

    result.set(
      viewportCol.column.publicId,
      rowDataArrayToObject(origRow.rowData, origIndexToColumnId, origRow._rowIndex, originalColumnStore)
    )
  }

  return result.size > 0 ? result : null
}
